import CaretDown16 from '@carbon/icons-react/es/caret--down/16';
import CaretUp16 from '@carbon/icons-react/es/caret--up/16';
import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import ChevronUp16 from '@carbon/icons-react/es/chevron--up/16';
import { Col, Divider, Row, Tooltip } from 'antd';
import { startCase } from 'lodash';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Operation } from 'api/permissions';
import { FieldValue, FieldValueType, FieldWithValue } from 'api/tasks';
import { Collapse, Panel } from 'components/atoms/collapse';
import { Loading } from 'components/atoms/loading';
import { Loader } from 'components/organisms/customer/sidebar/styled';
import { Renderer } from 'components/organisms/projects/project-overview/right-section/project-details/utils';
import {
  Content,
  FieldLabel,
  GhostFields,
  Help,
  LabelContainer,
} from 'components/organisms/task-pane/task-detail/task-fields/styled';
import { getFieldRenderer } from 'components/organisms/task-pane/task-detail/task-fields/task-custom-fields';
import {
  FieldType,
  filterableFieldType,
} from 'components/organisms/tasks/fields';
import { PLACEHOLDER, RoutePaths } from 'constant';
import { useFieldPermission } from 'hooks/permissions';
import { useTranslate } from 'hooks/use-translation';
import { useUserFieldSectionMapping } from './hooks';

const ChevronDown = styled(ChevronDown16)`
  fill: var(--blue-60);
  margin-left: -1em;
`;

const ChevronUp = styled(ChevronUp16)`
  fill: var(--blue-60);
  margin-left: -1em;
`;

const StyledHeader = styled.div`
  color: var(--blue-60);
  margin-left: var(--spacing-05);
`;

const StyledDivider = styled(Divider)`
  margin: 0;
`;

const StyledRow = styled(Row)`
  height: 80%;
`;

const StyledLoader = styled(Loader)`
  height: 100%;
`;

const ReadonlyContent = styled.div`
  padding: 0px var(--spacing-03);
`;

const StyledLink = styled(Link)`
  display: inline;
`;

const Center = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  color: var(--gray-70);
  padding: var(--spacing-04);
`;

const StyledSpan = styled.span`
  color: var(--coolest-gray-80);
  padding-left: var(--spacing-03);
`;

const StyledPanel = styled(Panel)`
  .ant-collapse-header {
    padding: 0 var(--spacing-05) !important;
  }
`;

const NamedCol = styled(Col)`
  display: flex;
  padding: 0 var(--spacing-03);

  > div:first-child {
    width: 30%;
    padding: var(--spacing-03);
  }

  > div:last-child {
    width: 70%;
  }
`;

const NamedRow = styled(Row)`
  ${NamedCol}:nth-child(2n) {
    background-color: var(--coolest-gray-10);
  }
`;

const GrayText = styled.span`
  color: var(--gray-50);
`;

const CreateFieldMsg = ({ canAccessField }): JSX.Element => {
  return canAccessField ? (
    <FormattedMessage
      id="settings.team_members.create_new_field_msg"
      values={{
        a: (chunks) => (
          <StyledLink to={RoutePaths.teamMemberFields}>{chunks}</StyledLink>
        ),
      }}
    />
  ) : (
    <></>
  );
};

const CollapsibleSections = ({
  readOnly,
  onFieldValueChange,
  activeKeys,
  fieldSectionMapping,
}): JSX.Element => {
  return (
    <>
      {fieldSectionMapping?.map((fs) => {
        return (
          <>
            <StyledDivider />
            <Collapse
              defaultActiveKey={activeKeys}
              expandIconPosition="right"
              className="teamMemberInfo"
              expandIcon={({ isActive }) => {
                return isActive ? <CaretUp16 /> : <CaretDown16 />;
              }}
            >
              <StyledPanel
                key={fs.fieldSectionId}
                header={<b>{fs.fieldSectionName}</b>}
              >
                <GhostFields>
                  <NamedRow gutter={[0, 4]} align="middle">
                    {fs.fieldOrder.length > 0 ? (
                      fs.fieldOrder
                        .map((field): [FieldWithValue, Renderer] => [
                          field,
                          getFieldRenderer(field),
                        ])
                        .map(([field, FieldRenderer]) => (
                          <NamedCol span="24">
                            <LabelContainer>
                              <FieldLabel>
                                {field.fieldLabel ?? startCase(field.fieldName)}
                                {field.description ? (
                                  <Tooltip title={field.description}>
                                    <Help />
                                  </Tooltip>
                                ) : null}
                              </FieldLabel>
                            </LabelContainer>
                            <Content data-cy={field.fieldLabel}>
                              {filterableFieldType(field) ===
                                FieldType.Boolean ||
                              filterableFieldType(field) ===
                                FieldType.SingleSelect ||
                              filterableFieldType(field) ===
                                FieldType.MultiSelect ||
                              filterableFieldType(field) ===
                                FieldType.SingleUser ||
                              filterableFieldType(field) ===
                                FieldType.MultiUser ||
                              !readOnly ? (
                                FieldRenderer && (
                                  <FieldRenderer
                                    disabled={readOnly}
                                    field={field}
                                    onChange={onFieldValueChange}
                                  />
                                )
                              ) : (
                                <ReadonlyContent>
                                  {field.value ?? (
                                    <StyledSpan>{PLACEHOLDER}</StyledSpan>
                                  )}
                                </ReadonlyContent>
                              )}
                            </Content>
                          </NamedCol>
                        ))
                    ) : (
                      <Center>
                        <GrayText>
                          <FormattedMessage id="settings.team_members.view.empty_section" />
                        </GrayText>
                      </Center>
                    )}
                  </NamedRow>
                </GhostFields>
              </StyledPanel>
            </Collapse>
          </>
        );
      })}
    </>
  );
};

export const TeamMemberSectionField: FC<{
  fields: Array<FieldValue<FieldValueType>> | undefined;
  readOnly: boolean;
  isProfileView?: boolean;
  onFieldValueChange?(fieldValue: any, { fieldId }: FieldWithValue): void;
}> = ({ fields, readOnly, isProfileView = false, onFieldValueChange }) => {
  const translate = useTranslate();
  const { isLoading, fieldSections, fieldSectionMapping } =
    useUserFieldSectionMapping(fields);

  const activeKeys = fieldSections?.map((r) => r.fieldSectionId);

  const canAccessField = useFieldPermission().includes(Operation.Update);

  const custom_section_title = translate(
    'settings.team_members.edit.section.title',
  );

  return (
    <>
      {isLoading ? (
        <StyledLoader>
          <Loading />
        </StyledLoader>
      ) : readOnly ? (
        activeKeys && activeKeys.length > 0 ? (
          <CollapsibleSections
            readOnly={readOnly}
            onFieldValueChange={onFieldValueChange}
            activeKeys={activeKeys}
            fieldSectionMapping={fieldSectionMapping}
          />
        ) : (
          !isProfileView && (
            <>
              <Divider />
              <StyledRow align="middle">
                <Col offset={6} span={16}>
                  <GrayText>
                    <FormattedMessage
                      id="settings.team_members.empty_custom_fields_msg"
                      values={{
                        create_new_field: (
                          <CreateFieldMsg canAccessField={canAccessField} />
                        ),
                      }}
                    />
                  </GrayText>
                </Col>
              </StyledRow>
            </>
          )
        )
      ) : (
        activeKeys &&
        activeKeys.length > 0 && (
          <Collapse
            defaultActiveKey={custom_section_title}
            expandIcon={({ isActive }) => {
              return isActive ? <ChevronUp /> : <ChevronDown />;
            }}
            expandIconPosition="right"
          >
            <Panel
              key={custom_section_title}
              header={<StyledHeader>{custom_section_title}</StyledHeader>}
            >
              <CollapsibleSections
                readOnly={readOnly}
                onFieldValueChange={onFieldValueChange}
                activeKeys={activeKeys}
                fieldSectionMapping={fieldSectionMapping}
              />
            </Panel>
          </Collapse>
        )
      )}
    </>
  );
};
